import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { isNil } from 'lodash';
import { fromEvent, Observable, Subject } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';

import { BuildingsStoreService } from '@app/energy-map/services/buildings-store.service';
import { MapStoreService } from '@app/energy-map/services/map-store.service';
import { AuthService } from '@app/shared/services/auth.service';
import { formatBuildingAddress } from '@app/shared/utility';
import { EnergyMarker } from '@app/shared/models/energiekarte/epmarker';

@Component({
  selector: 'app-edit-location',
  templateUrl: './edit-location.component.html',
  styleUrls: ['./edit-location.component.scss'],
})
export class EditLocationComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Input() building: EnergyMarker = {} as EnergyMarker;
  @Output() closeEvent = new EventEmitter();

  private draggableMarker?: L.Marker;

  constructor(private mapStoreService: MapStoreService, public authService: AuthService, private buildingStoreService: BuildingsStoreService) {}

  public ngOnInit(): void {
    this.building.fullAddress = formatBuildingAddress(this.building);
    this.buildingStoreService.selectedBuilding$
      .pipe(
        filter((selectedBuilding) => isNil(selectedBuilding)),
        first()
      )
      .subscribe(() => this.closeDialog());
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public localizeObject(marker: L.Marker): void {
    marker.remove();
    if (!this.draggableMarker) {
      this.draggableMarker = this.createMarker(
        this.createDragIcon(this.building)
        // this.building.marker.getLatLng() was unused
      ).addTo(this.mapStoreService.map as L.Map);
    } else {
      this.draggableMarker.setLatLng((this.mapStoreService.map as L.Map).getCenter());
    }

    (this.mapStoreService.map as L.Map).panTo((this.mapStoreService.map as L.Map).getCenter());
    this.onBuildingDrag(this.draggableMarker).pipe(takeUntil(this.unsubscribe$)).subscribe();
    this.onBuildingDragEnd(this.draggableMarker)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((e: any) => (this.mapStoreService.map as L.Map).panTo(e.target.getLatLng()));
  }

  private createMarker = (icon: L.Icon): L.Marker =>
    L.marker((this.mapStoreService.map as L.Map).getCenter(), {
      icon: icon,
      draggable: true,
    });

  private createDragIcon(building: any): L.Icon {
    const iconName = this.getIconImageName(building);
    const icon = L.icon({ iconUrl: this.getDragIconUrl(iconName) });
    if (iconName === 'cube-v2.png') {
      icon.options.iconAnchor = [30, 120];
      icon.options.iconSize = [120, 120];
    } else {
      icon.options.iconAnchor = [45, 90];
    }
    return icon;
  }

  private getIconImageName = (building: any): string => (building.buildingType ? building.buildingType.imageName : 'cube-v2.png');

  private getDragIconUrl = (imageName: string): string => `/assets/icons/buildings/drag/${imageName}`;

  private onBuildingDrag = (marker: L.Marker): Observable<L.LeafletEvent> => fromEvent(marker, 'drag');

  private onBuildingDragEnd = (marker: L.Marker): Observable<L.LeafletEvent> => fromEvent(marker, 'dragend');

  public save(building: any): void {
    if (this.draggableMarker) {
      const latlng = this.draggableMarker.getLatLng();
      this.buildingStoreService.updateBuildingLatLng(this.updateBuildingLatLng(this.building, latlng.lat, latlng.lng));
      this.mapStoreService.consumerCluster.addLayer(this.building.marker);
      this.removeDraggableMarker(this.draggableMarker);
    }
    this.closeEvent.emit();
  }

  public closeDialog(): void {
    this.building.marker?.addTo(this.mapStoreService.map as L.Map as L.Map);
    (this.mapStoreService.map as L.Map)?.panTo(new L.LatLng(this.building.latitude, this.building.longitude));
    if (this.draggableMarker) {
      this.removeDraggableMarker(this.draggableMarker);
    }
    this.closeEvent.emit();
  }

  private updateBuildingLatLng = (building: any, lat: number, lng: number): any => {
    building.latitude = lat;
    building.longitude = lng;
    building.marker?.setLatLng([lat, lng]);

    return building;
  };

  private removeDraggableMarker = (marker: L.Marker): L.Marker => {
    if (marker) {
      marker.remove();
    }

    return marker;
  };
}
